import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CloudSun,
  Compass,
  Fish,
  HelpCircle,
  Home,
  LifeBuoy,
  Map as MapIcon,
  MessageCircle,
  PenSquare,
  Search,
  Trophy,
  UserCircle,
  Users,
  X,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "../styles/pages/UserManual.css";
import DesktopRightRail from "../components/DesktopRightRail";

const MANUAL_SECTIONS = [
  {
    key: "overview",
    labelKey: "user_manual_section_overview",
    defaultLabel: "Overview",
    file: "user-manual-overview.md",
  },
  {
    key: "feed-map-weather",
    labelKey: "user_manual_section_feed_map_weather",
    defaultLabel: "Feed, Map, Weather & Tides",
    file: "user-manual-feed-map-weather.md",
  },
  {
    key: "post-catch",
    labelKey: "user_manual_section_post_catch",
    defaultLabel: "Post Catch",
    file: "user-manual-post-catch.md",
  },
  {
    key: "fishipedia-species-regulations",
    labelKey: "user_manual_section_fishipedia_species_regulations",
    defaultLabel: "Fishipedia, Species & Regulations",
    file: "user-manual-fishipedia-species-regulations.md",
  },
  {
    key: "groups-messages-search",
    labelKey: "user_manual_section_groups_messages_search",
    defaultLabel: "Groups, Messages & Search",
    file: "user-manual-groups-messages-search.md",
  },
  {
    key: "profile-rankings-feedback",
    labelKey: "user_manual_section_profile_rankings_feedback",
    defaultLabel: "Profile, Rankings & Feedback",
    file: "user-manual-profile-rankings-feedback.md",
  },
  {
    key: "trips-planner",
    labelKey: "user_manual_section_trips_planner",
    defaultLabel: "Trips & Planner",
    file: "user-manual-trips-planner.md",
  },
  {
    key: "whats-next",
    labelKey: "user_manual_section_whats_next",
    defaultLabel: "What's Next",
    file: "user-manual-whats-next.md",
  },
];

const MANUAL_INDEX_GROUPS = [
  {
    key: "getting-started",
    titleKey: "user_manual_group_getting_started",
    items: [
      {
        key: "home-overview",
        sectionKey: "overview",
        titleKey: "user_manual_index_home_overview",
        descriptionKey: "user_manual_index_home_overview_desc",
        icon: Home,
      },
      {
        key: "profile",
        sectionKey: "profile-rankings-feedback",
        titleKey: "user_manual_index_profile",
        descriptionKey: "user_manual_index_profile_desc",
        icon: UserCircle,
      },
      {
        key: "notifications",
        sectionKey: "profile-rankings-feedback",
        titleKey: "user_manual_index_notifications",
        descriptionKey: "user_manual_index_notifications_desc",
        icon: Bell,
      },
      {
        key: "messages",
        sectionKey: "groups-messages-search",
        titleKey: "user_manual_index_messages",
        descriptionKey: "user_manual_index_messages_desc",
        icon: MessageCircle,
      },
    ],
  },
  {
    key: "features",
    titleKey: "user_manual_group_features",
    items: [
      {
        key: "map",
        sectionKey: "feed-map-weather",
        titleKey: "user_manual_index_map",
        descriptionKey: "user_manual_index_map_desc",
        icon: MapIcon,
      },
      {
        key: "post-catch",
        sectionKey: "post-catch",
        titleKey: "user_manual_index_post_catch",
        descriptionKey: "user_manual_index_post_catch_desc",
        icon: PenSquare,
      },
      {
        key: "weather-tides",
        sectionKey: "feed-map-weather",
        titleKey: "user_manual_index_weather_tides",
        descriptionKey: "user_manual_index_weather_tides_desc",
        icon: CloudSun,
      },
      {
        key: "groups",
        sectionKey: "groups-messages-search",
        titleKey: "user_manual_index_groups",
        descriptionKey: "user_manual_index_groups_desc",
        icon: Users,
      },
      {
        key: "rankings",
        sectionKey: "profile-rankings-feedback",
        titleKey: "user_manual_index_rankings",
        descriptionKey: "user_manual_index_rankings_desc",
        icon: Trophy,
      },
      {
        key: "trips-planner",
        sectionKey: "trips-planner",
        titleKey: "user_manual_index_trips_planner",
        descriptionKey: "user_manual_index_trips_planner_desc",
        icon: Compass,
      },
    ],
  },
  {
    key: "more",
    titleKey: "user_manual_group_more",
    items: [
      {
        key: "fishipedia",
        sectionKey: "fishipedia-species-regulations",
        titleKey: "user_manual_index_fishipedia",
        descriptionKey: "user_manual_index_fishipedia_desc",
        icon: Fish,
      },
      {
        key: "faq",
        sectionKey: "whats-next",
        titleKey: "user_manual_index_faq",
        descriptionKey: "user_manual_index_faq_desc",
        icon: HelpCircle,
      },
      {
        key: "contact-support",
        sectionKey: "overview",
        titleKey: "user_manual_index_contact_support",
        descriptionKey: "user_manual_index_contact_support_desc",
        icon: LifeBuoy,
      },
    ],
  },
];

function getSectionByKey(sectionKey) {
  return (
    MANUAL_SECTIONS.find((section) => section.key === sectionKey) ||
    MANUAL_SECTIONS[0]
  );
}

export default function UserManual({
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeSectionKey, setActiveSectionKey] = useState(
    MANUAL_SECTIONS[0].key
  );
  const [isArticleOpen, setIsArticleOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);
  const markdownCacheRef = useRef(new Map());

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  const activeSection = useMemo(
    () => getSectionByKey(activeSectionKey),
    [activeSectionKey]
  );

  const closeUtilityPage = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      Number(window.history.state.idx) > 0
    ) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }, [navigate]);

  const manualLocale = useMemo(() => {
    const rawLanguage =
      i18n.resolvedLanguage || i18n.language || i18n.languages?.[0] || "en";
    return String(rawLanguage).toLowerCase().startsWith("bg") ? "BG" : "EN";
  }, [i18n.language, i18n.languages, i18n.resolvedLanguage]);

  const indexGroups = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return MANUAL_INDEX_GROUPS.map((group) => {
      const items = group.items
        .map((item) => {
          const section = getSectionByKey(item.sectionKey);
          const title = t(item.titleKey);
          const description = t(item.descriptionKey);
          const sectionLabel = t(section.labelKey, section.defaultLabel);

          return {
            ...item,
            section,
            title,
            description,
            sectionLabel,
          };
        })
        .filter((item) => {
          if (!normalizedQuery) return true;

          return `${item.title} ${item.description} ${item.sectionLabel}`
            .toLowerCase()
            .includes(normalizedQuery);
        });

      return {
        ...group,
        title: t(group.titleKey),
        items,
      };
    }).filter((group) => group.items.length > 0);
  }, [searchQuery, t]);

  useEffect(() => {
    if (!isArticleOpen) {
      setIsLoading(false);
      setError("");
      return undefined;
    }

    let isMounted = true;
    const controller = new AbortController();

    async function loadManualSection() {
      setIsLoading(true);
      setError("");

      const cacheKey = `${manualLocale}:${activeSection.key}`;
      const cachedMarkdown = markdownCacheRef.current.get(cacheKey);
      if (cachedMarkdown) {
        setMarkdown(cachedMarkdown);
        setIsLoading(false);
        return;
      }

      try {
        const candidateLocales =
          manualLocale === "EN" ? ["EN"] : [manualLocale, "EN"];

        let nextMarkdown = "";
        let resolved = false;

        for (const locale of candidateLocales) {
          const response = await fetch(
            `${process.env.PUBLIC_URL}/Markdown/user-manual/${locale}/${activeSection.file}`,
            { signal: controller.signal }
          );

          if (!response.ok) {
            continue;
          }

          const candidateMarkdown = await response.text();
          if (!candidateMarkdown.trim()) {
            continue;
          }

          nextMarkdown = candidateMarkdown;
          resolved = true;
          break;
        }

        if (!resolved) {
          throw new Error("User manual section markdown could not be resolved");
        }

        markdownCacheRef.current.set(cacheKey, nextMarkdown);

        if (isMounted) {
          setMarkdown(nextMarkdown);
        }
      } catch (loadError) {
        if (loadError.name === "AbortError") {
          return;
        }

        console.error("User manual markdown load error:", loadError);

        if (isMounted) {
          setMarkdown("");
          setError(
            t(
              "user_manual_load_error",
              "This manual section could not be loaded right now."
            )
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadManualSection();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [activeSection.file, activeSection.key, isArticleOpen, manualLocale, t]);

  const markdownComponents = useMemo(
    () => ({
      a: ({ href = "", children, ...props }) => {
        const isExternal = /^https?:\/\//i.test(href);

        return (
          <a
            href={href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            {...props}
          >
            {children}
          </a>
        );
      },
      img: ({ src = "", alt = "", ...props }) => (
        <img src={src} alt={alt} loading="lazy" {...props} />
      ),
    }),
    []
  );

  const openManualSection = (sectionKey) => {
    setActiveSectionKey(sectionKey);
    setIsArticleOpen(true);
  };

  const desktopRightRail = enableDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`user-manual-layout${
        enableDesktopRightRail ? " user-manual-layout--with-right-rail" : ""
      }`}
    >
      <div className="user-manual-shell">
        <div className="user-manual-page">
        <button
          type="button"
          className="user-manual-close"
          onClick={closeUtilityPage}
          aria-label={t("common_close")}
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>
        {!isArticleOpen ? (
          <>
            <header className="user-manual-hero">
              <div className="user-manual-hero-copy">
                <span className="user-manual-eyebrow">
                  {t("user_manual_home_eyebrow")}
                </span>
                <h2 className="user-manual-title">{t("user_manual_title")}</h2>
                <p>{t("user_manual_home_subtitle")}</p>
              </div>
              <span className="user-manual-hero-icon" aria-hidden="true">
                <BookOpen size={38} strokeWidth={1.9} />
              </span>
            </header>

            <label className="user-manual-search" htmlFor="user-manual-search">
              <Search size={18} strokeWidth={2.2} aria-hidden="true" />
              <input
                id="user-manual-search"
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={t("user_manual_search_placeholder")}
              />
            </label>

            <div className="user-manual-index">
              {indexGroups.length > 0 ? (
                indexGroups.map((group) => (
                  <section className="user-manual-index-group" key={group.key}>
                    <h3>{group.title}</h3>
                    <div className="user-manual-index-list">
                      {group.items.map((item) => {
                        const Icon = item.icon;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            className="user-manual-index-card"
                            onClick={() => openManualSection(item.sectionKey)}
                          >
                            <span
                              className="user-manual-index-card-icon"
                              aria-hidden="true"
                            >
                              <Icon size={21} strokeWidth={2.1} />
                            </span>
                            <span className="user-manual-index-card-copy">
                              <span className="user-manual-index-card-title">
                                {item.title}
                              </span>
                              <span className="user-manual-index-card-description">
                                {item.description}
                              </span>
                            </span>
                            <ChevronRight
                              className="user-manual-index-card-chevron"
                              size={19}
                              strokeWidth={2.4}
                              aria-hidden="true"
                            />
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))
              ) : (
                <p className="user-manual-state">
                  {t("user_manual_search_empty")}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="user-manual-article-topbar">
              <button
                type="button"
                className="user-manual-back"
                onClick={() => setIsArticleOpen(false)}
              >
                <ChevronLeft size={18} strokeWidth={2.4} aria-hidden="true" />
                <span>{t("common_back")}</span>
              </button>
              <span className="user-manual-article-kicker">
                {t("user_manual_sections_label", "Manual sections")}
              </span>
            </div>

            <div
              className="user-manual-nav"
              aria-label={t("user_manual_sections_label", "Manual sections")}
            >
              {MANUAL_SECTIONS.map((section) => {
                const isActive = section.key === activeSection.key;

                return (
                  <button
                    key={section.key}
                    type="button"
                    className={`user-manual-nav-button ${
                      isActive ? "active" : ""
                    }`}
                    aria-pressed={isActive}
                    onClick={() => openManualSection(section.key)}
                  >
                    {t(section.labelKey, section.defaultLabel)}
                  </button>
                );
              })}
            </div>

            <div className="user-manual-content">
              {isLoading ? (
                <p className="user-manual-state">
                  {t("user_manual_loading", "Loading manual section...")}
                </p>
              ) : error ? (
                <p className="user-manual-state user-manual-state-error">
                  {error}
                </p>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {markdown}
                </ReactMarkdown>
              )}
            </div>
          </>
        )}

        <div className="user-manual-legal">
          <h3 className="user-manual-legal-title">
            {t("user_manual_legal_title", { defaultValue: "Legal" })}
          </h3>
          <p className="user-manual-legal-copy">
            {t("user_manual_legal_copy", {
              defaultValue:
                "Review the current legal documents and data attribution details.",
            })}
          </p>
          <div className="user-manual-legal-links">
            <Link to="/legal/terms">
              {t("legal_document_terms_title", {
                defaultValue: "Terms and Conditions",
              })}
            </Link>
            <Link to="/legal/privacy">
              {t("legal_document_privacy_title", {
                defaultValue: "Privacy Policy and User Consent",
              })}
            </Link>
            <Link to="/legal/attributions">
              {t("legal_document_attributions_title", {
                defaultValue: "API and Data Attributions",
              })}
            </Link>
          </div>
        </div>

        <div className="user-manual-bottom-actions">
          <button
            type="button"
            className="user-manual-bottom-close"
            onClick={closeUtilityPage}
          >
            {t("common_close")}
          </button>
        </div>
        </div>
      </div>
      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}
    </div>
  );
}
